/**
 * ContactPeopleSection — Sección de contactos humanos al pie del landing.
 * Replica el bloque "Hablemos" del landing de turismo (turismo.nara.chat)
 * pero adaptado a Care (health.nara.chat) y usando el sistema de MUI +
 * landingTheme para consistencia visual con el resto de la landing.
 */
import React from 'react';
import { Box, Container, Typography, Button, Stack } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaceIcon from '@mui/icons-material/Place';
import LanguageIcon from '@mui/icons-material/Language';

import { COLORS, TYPOGRAPHY, RADIUS, TRANSITIONS } from './landingTheme';
import { ScrollReveal, StaggerReveal } from './animations';

// Datos de los 3 contactos — mismos que en el landing de turismo,
// misma fuente de verdad (equipo NARA México/Colombia).
const PEOPLE = [
  {
    role: 'Co-founder & CEO',
    name: 'Hernando Olier',
    channels: [
      { kind: 'phone', country: 'MX', label: '+52 722 186 1320', href: 'tel:+527221861320' },
      { kind: 'wa',    country: 'CO', label: '+57 312 552 2112', href: 'https://wa.me/573125522112' },
    ],
  },
  {
    role: 'Co-founder & Chief Technology Officer',
    name: 'Raul Olier P.',
    channels: [
      { kind: 'wa', country: 'CO', label: '+57 305 260 8968', href: 'https://wa.me/573052608968' },
    ],
  },
  {
    role: 'Strategic Sales Partner',
    name: 'David Antonio Corrales',
    channels: [
      { kind: 'wa', country: 'CO', label: '+57 323 286 5440', href: 'https://wa.me/573232865440' },
    ],
  },
];

// CTA principal — al WhatsApp de Raul, mensaje pre-cargado para Care
const CTA_WHATSAPP = 'https://wa.me/573052608968?text=' + encodeURIComponent(
  'Hola Raul, quiero agendar una demo de NARA para mi consultorio.'
);

function ChannelChip({ channel }) {
  const isWa = channel.kind === 'wa';
  const Icon = isWa ? WhatsAppIcon : PhoneIcon;
  return (
    <Button
      component="a"
      href={channel.href}
      target={isWa ? '_blank' : undefined}
      rel={isWa ? 'noopener noreferrer' : undefined}
      startIcon={<Icon sx={{ fontSize: '16px !important' }} />}
      sx={{
        textTransform: 'none',
        justifyContent: 'flex-start',
        px: 1.5,
        py: 0.75,
        borderRadius: 999,
        fontSize: 12.5,
        fontWeight: 600,
        color: isWa ? '#166534' : COLORS.textSecondary,
        bgcolor: isWa ? '#dcfce7' : COLORS.gray100,
        border: `1px solid ${isWa ? '#bbf7d0' : COLORS.gray200}`,
        transition: TRANSITIONS.default,
        '&:hover': {
          transform: 'translateY(-1px)',
          bgcolor: isWa ? '#bbf7d0' : COLORS.gray200,
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
        },
      }}
    >
      <Box component="span" sx={{
        fontSize: 9.5, fontWeight: 700, opacity: 0.65,
        textTransform: 'uppercase', letterSpacing: 0.5, mr: 0.75,
      }}>
        {channel.country}{isWa ? ' · WhatsApp' : ''}
      </Box>
      {channel.label}
    </Button>
  );
}

function PersonBlock({ person, index }) {
  return (
    <Box sx={{
      py: 2,
      borderBottom: index < PEOPLE.length - 1 ? `1px solid ${COLORS.gray200}` : 'none',
    }}>
      <Typography sx={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        color: COLORS.accent,
        mb: 0.4,
      }}>
        {person.role}
      </Typography>
      <Typography sx={{
        fontSize: 18,
        fontWeight: 700,
        color: COLORS.textPrimary,
        mb: 1.25,
        letterSpacing: '-0.01em',
      }}>
        {person.name}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {person.channels.map((c, i) => (
          <ChannelChip key={i} channel={c} />
        ))}
      </Stack>
    </Box>
  );
}

export default function ContactPeopleSection() {
  return (
    <Box
      id="contacto"
      sx={{
        scrollMarginTop: 70,
        py: { xs: 6, md: 10 },
        background: `linear-gradient(180deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
        color: COLORS.white,
        position: 'relative',
      }}
    >
      {/* Sutil textura de puntos para dar profundidad */}
      <Box aria-hidden="true" sx={{
        position: 'absolute', inset: 0,
        opacity: 0.05,
        backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        pointerEvents: 'none',
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
          gap: { xs: 4, md: 8 },
          alignItems: 'center',
        }}>
          {/* Columna izquierda: pitch + CTA */}
          <ScrollReveal>
            <Box>
              <Typography sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                color: COLORS.accent,
                mb: 1.5,
              }}>
                Hablemos
              </Typography>
              <Typography variant="h2" sx={{
                fontSize: { xs: 28, sm: 32, md: 40 },
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                color: COLORS.white,
                mb: 2,
              }}>
                La conversación empieza con{' '}
                <Box component="em" sx={{
                  fontStyle: 'normal',
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accentLight} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  una demo de 30 minutos.
                </Box>
              </Typography>
              <Typography sx={{
                fontSize: { xs: 14.5, md: 15.5 },
                lineHeight: 1.6,
                color: 'rgba(255,255,255,0.78)',
                maxWidth: 480,
                mb: 3,
              }}>
                Ven con tu operación actual y una hora libre. Te mostramos NARA con datos
                reales de un consultorio ya en producción y lo comparamos con lo que haces hoy.
              </Typography>

              <Button
                component="a"
                href={CTA_WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsAppIcon sx={{ fontSize: '20px !important' }} />}
                sx={{
                  textTransform: 'none',
                  fontSize: 15,
                  fontWeight: 700,
                  color: COLORS.primaryDark,
                  bgcolor: COLORS.white,
                  borderRadius: 999,
                  px: 3,
                  py: 1.4,
                  minHeight: 48,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                  transition: TRANSITIONS.default,
                  '&:hover': {
                    bgcolor: COLORS.white,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.24)',
                  },
                }}
              >
                Agendar demo por WhatsApp →
              </Button>
            </Box>
          </ScrollReveal>

          {/* Columna derecha: card blanca con los 3 contactos */}
          <ScrollReveal delay={0.15}>
            <Box sx={{
              bgcolor: COLORS.white,
              borderRadius: RADIUS?.large || 3,
              p: { xs: 2.5, md: 3.5 },
              boxShadow: '0 24px 60px -12px rgba(0,0,0,0.35)',
              border: `1px solid ${COLORS.gray200}`,
            }}>
              {PEOPLE.map((p, i) => (
                <PersonBlock key={i} person={p} index={i} />
              ))}

              {/* Footer meta: sitio + ubicación */}
              <Box sx={{
                mt: 2, pt: 2, borderTop: `1px solid ${COLORS.gray200}`,
                display: 'flex', flexWrap: 'wrap', gap: 2,
                fontSize: 12, color: COLORS.textMuted,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LanguageIcon sx={{ fontSize: 14, color: COLORS.gray400 }} />
                  <Box component="strong" sx={{ color: COLORS.textPrimary, fontWeight: 700 }}>
                    Sitio
                  </Box>
                  <Box component="span">health.nara.chat</Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PlaceIcon sx={{ fontSize: 14, color: COLORS.gray400 }} />
                  <Box component="strong" sx={{ color: COLORS.textPrimary, fontWeight: 700 }}>
                    Base
                  </Box>
                  <Box component="span">México DF · Barranquilla · Cartagena</Box>
                </Box>
              </Box>
            </Box>
          </ScrollReveal>
        </Box>
      </Container>
    </Box>
  );
}
